import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, RotateCcw, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ActionRequired = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/action-required`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelAndRelist = async (item) => {
    const key = item.order_id || item.product_id;
    if (!window.confirm(`Cancel this ${item.type === 'unpaid_order' ? 'unpaid order' : 'orphan listing'} and put "${item.product_title}" back on sale?`)) return;
    setBusy(prev => ({ ...prev, [key]: true }));
    try {
      const url = item.type === 'unpaid_order'
        ? `${BACKEND_URL}/api/admin/orders/${item.order_id}/cancel`
        : `${BACKEND_URL}/api/admin/products/${item.product_id}/unmark-sold`;
      const res = await fetch(url, { method: 'POST' });
      if (res.ok) {
        toast.success(`"${item.product_title}" is back on sale`);
        await load();
      } else {
        toast.error('Action failed — please try again');
      }
    } catch (e) {
      toast.error('Action failed — please try again');
    } finally {
      setBusy(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="rounded-2xl p-5 border" style={{ backgroundColor: '#fdf8ee', borderColor: '#c9a962' }} data-testid="action-required-panel">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4" style={{ color: '#b8860b' }} />
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: '#8a6914' }}>
          Action Required ({items.length})
        </h3>
      </div>
      <p className="text-xs text-stone-600 mb-4">
        These items are held as sold but no payment has been recorded. Pickup orders may have been paid in cash — only cancel if the sale fell through.
      </p>
      <div className="space-y-3">
        {items.map((item) => {
          const key = item.order_id || item.product_id;
          return (
            <div key={key} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-stone-200" data-testid={`action-item-${key}`}>
              {item.product_image ? (
                <img src={item.product_image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-stone-100 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-stone-800 truncate">{item.product_title}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide" style={{ backgroundColor: item.type === 'unpaid_order' ? '#fde8e8' : '#fef3cd', color: item.type === 'unpaid_order' ? '#b91c1c' : '#8a6914' }}>
                    {item.type === 'unpaid_order' ? 'Unpaid Order' : 'Held, No Order'}
                  </span>
                </div>
                <p className="text-xs text-stone-500 truncate">
                  {item.type === 'unpaid_order'
                    ? <>Buyer: {item.buyer_name} · ${Number(item.amount || 0).toFixed(2)} · {item.shipping_method}
                        {item.days_waiting > 0 && <span className="inline-flex items-center gap-0.5 ml-1"><Clock className="w-3 h-3 inline" /> {item.days_waiting}d waiting</span>}
                      </>
                    : <>Seller: {item.seller_name} · ${Number(item.amount || 0).toFixed(2)} · marked sold with no matching order</>}
                </p>
              </div>
              <button
                onClick={() => cancelAndRelist(item)}
                disabled={busy[key]}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white flex-shrink-0 transition-colors disabled:opacity-60"
                style={{ backgroundColor: '#1a3a2a' }}
                data-testid={`cancel-relist-btn-${key}`}
              >
                {busy[key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Cancel & Relist
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
